<template>
  <ActionViewContainer
    :title-icon="getIconForOperationSettingsItem('automations')"
    :breadcrumbsItems="formattedBreadcrumbs"
    :actions="actions"
    :entryData="entryData"
    @add-automation="openAddModal"
    title="attach_automations"
  >
    <template slot="table">
      <v-data-table
        :headers="headers"
        :items="nodeAutomations"
        :loading="loading"
        class="elevation-0"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item.automation_key="{ item }">
          <span>{{ item.automation_key }}</span><br>
          <span v-if="isInheritedAutomation(item)">({{ $t('inherited') }})</span>
          <span v-else-if="item.recursive">({{ $t('recursive') }})</span>
        </template>

        <template v-slot:item.automation_trigger="{ item }">
          {{ $t(item.automation_trigger) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <template v-if="!isInheritedAutomation(item)">
            <v-icon class="mr-2" @click.stop="editAutomation(item)">mdi-pencil</v-icon>
            <v-icon class="mr-2" @click.stop="removeAutomation(item)">mdi-minus-circle</v-icon>
          </template>
          <template v-else>-</template>
        </template>
      </v-data-table>

      <DefaultModal
        :title="$t('attach_automations')"
        ref="modal"
        @submit="saveAutomation"
        :loading="loading"
      >
        <template v-slot:content>
          <v-select
            v-model="selectedAutomationId"
            :items="allAutomations"
            item-text="automation_key"
            item-value="automation_id"
            :label="$t('automations')"
            :readonly="editing"
            outlined
          />
          <v-switch :label="$t('recursive')" v-model="recursive" />
        </template>
      </DefaultModal>
    </template>
  </ActionViewContainer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import ActionViewContainer from '@/components/content/ActionViewContainer'
import DefaultModal from '@/components/DefaultModal'
import EntryActions from '@/store/content/entry/actions-types'
import AutomationActions from '@/store/operation/automation/actions-types'

export default {
  name: 'AttachAutomations',

  components: {
    ActionViewContainer,
    DefaultModal,
  },

  data() {
    return {
      loading: false,
      actions: [{ text: 'add_automation', eventToEmit: 'add-automation' }],
      entryData: {},
      headers: [
        { text: 'Key', value: 'automation_key' },
        { text: 'Pipeline ID', value: 'pipeline_id' },
        { text: 'Trigger', value: 'automation_trigger' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false },
      ],
      nodeAutomations: [],
      allAutomations: [],
      selectedAutomationId: null,
      recursive: true,
      editing: false,
    }
  },

  computed: {
    ...mapGetters('app', ['getIconForOperationSettingsItem']),

    ...mapState({
      breadcrumb: state => state.content.entry.breadcrumb,
    }),

    formattedBreadcrumbs() {
      const formatted = [{
        prependIcon: 'mdi-home-outline',
        iconColor: 'primary',
        to: { name: 'ContentHome', params: { appId: this.$route.params.appId } },
        link: true,
        disabled: false,
        ripple: true,
      }]
      const reversed = [].concat(this.breadcrumb).reverse()

      reversed.forEach((element, index, array) => {
        formatted.push({
          to: { name: 'ContentNavigation', params: { appId: this.$route.params.appId, parentId: element.node_id } },
          text: element.name,
          ripple: true,
          disabled: Object.is(array.length - 1, index),
        })
      })

      return formatted
    },
  },

  mounted() {
    this.loadData()
  },

  methods: {
    ...mapActions('content/entry', [
      EntryActions.LIST_SUBFOLDER,
      EntryActions.GET_ENTRY_DATA,
    ]),

    ...mapActions('operation/automation', [
      AutomationActions.GET_AUTOMATIONS_BY_NODE,
      AutomationActions.GET_ALL_AUTOMATIONS,
      AutomationActions.ADD_AUTOMATION_NODE_RELATIONSHIP,
      AutomationActions.REMOVE_AUTOMATION_NODE_RELATIONSHIP,
    ]),

    loadData() {
      this.loading = true
      const nodeId = this.$route.params.nodeId

      Promise.all([
        this[EntryActions.LIST_SUBFOLDER]({ parentId: nodeId }),
        this[EntryActions.GET_ENTRY_DATA](nodeId).then(data => { this.entryData = data }),
        this[AutomationActions.GET_AUTOMATIONS_BY_NODE](nodeId).then(data => { this.nodeAutomations = data.items || [] }),
      ]).finally(() => { this.loading = false })
    },

    openAddModal() {
      this.editing = false
      this.selectedAutomationId = null
      this.recursive = true
      this.openModal()
    },

    openModal() {
      this.loading = true
      this[AutomationActions.GET_ALL_AUTOMATIONS]({ order: 'automation_key', direction: 'ASC' })
        .then(data => { this.allAutomations = data.items || [] })
        .finally(() => { this.loading = false })
      this.$refs.modal.openModal()
    },

    saveAutomation() {
      this[AutomationActions.ADD_AUTOMATION_NODE_RELATIONSHIP]({
        nodeId: this.$route.params.nodeId,
        automationId: this.selectedAutomationId,
        data: { recursive: this.recursive === true, item_order: 1, filter_id: null },
      })
        .then(() => {
          this.$refs.modal.closeModal()
          this.loadData()
        })
        .finally(() => { this.$refs.modal.submitting = false })
    },

    editAutomation(item) {
      this.editing = true
      this.selectedAutomationId = item.automation_id
      this.recursive = item.recursive
      this.openModal()
    },

    removeAutomation(item) {
      this.loading = true
      this[AutomationActions.REMOVE_AUTOMATION_NODE_RELATIONSHIP]({
        nodeId: this.$route.params.nodeId,
        automationId: item.automation_id,
      })
        .then(() => this.loadData())
        .catch(() => { this.loading = false })
    },

    isInheritedAutomation(automation) {
      const relationNodeId = automation.relation_node_id

      return relationNodeId !== null &&
        relationNodeId !== undefined &&
        String(relationNodeId) !== String(this.$route.params.nodeId)
    },
  },
}
</script>
